import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ModalShell, ModalBody } from '@/components/ui/common/Modal';
import { useSessionTakeoverStore } from '@/store/sessionTakeoverStore';
import { clearAuthAndRedirect } from '@/api/core/axios';

const CountdownNumber = styled.div`
  margin: ${({ theme }) => theme.spacing[5]}px auto ${({ theme }) => theme.spacing[4]}px;
  font-size: 72px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: 1;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.emphasis};
  font-variant-numeric: tabular-nums;
`;

const HintLine = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

/**
 * Shown when refresh fails or JWT is rejected after re-login elsewhere (same account).
 */
export const SessionTakeoverModal: React.FC = () => {
  const { t } = useTranslation('auth');
  const isOpen = useSessionTakeoverStore(s => s.isOpen);
  const close = useSessionTakeoverStore(s => s.close);
  const [shown, setShown] = useState(3);

  useEffect(() => {
    if (!isOpen) return;

    setShown(3);
    const after1 = window.setTimeout(() => setShown(2), 1000);
    const after2 = window.setTimeout(() => setShown(1), 2000);
    const after3 = window.setTimeout(() => {
      close();
      clearAuthAndRedirect();
    }, 5000);

    return () => {
      window.clearTimeout(after1);
      window.clearTimeout(after2);
      window.clearTimeout(after3);
    };
  }, [isOpen, close]);

  return (
    <ModalShell
      isOpen={isOpen}
      title={t('session_takeover_title')}
      size="sm"
      showCloseButton={false}
      onClose={() => {}}
    >
      <ModalBody>{t('session_takeover_message')}</ModalBody>
      <CountdownNumber aria-live="polite">{shown}</CountdownNumber>
      <HintLine>{t('session_takeover_footer')}</HintLine>
    </ModalShell>
  );
};
